import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Upload, Sparkles, HelpCircle, CheckCircle2, Loader2, Phone, X, Wand2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";
import ProgressBar from "../components/builder/ProgressBar";
import { OptionCard, CompactChip } from "../components/builder/OptionCard";
import PhotoField from "../components/builder/PhotoField";
import IntroScreen from "../components/builder/IntroScreen";
import QuickEstimate from "../components/builder/QuickEstimate";
import {
  STEPS,
  LAYOUT_OPTIONS,
  CONDITION_OPTIONS,
  STYLE_OPTIONS,
  TV_STATUS_OPTIONS,
  TV_MOUNT_OPTIONS,
  MAIN_FINISH_OPTIONS,
  ACCENT_OPTIONS,
  ACCENT_SIDE_OPTIONS,
  COLOR_OPTIONS,
  TRIM_OPTIONS,
  FIREPLACE_OPTIONS,
  FIREPLACE_WIDTH_OPTIONS,
  CONSOLE_OPTIONS,
  TOWER_OPTIONS,
  CUBBY_PRESETS,
  LIGHTING_OPTIONS,
  LIGHTING_COLOR_OPTIONS,
  EQUIPMENT_OPTIONS,
  BUDGET_OPTIONS,
  TIMING_OPTIONS,
  CONTACT_METHODS,
} from "../data/wallBuilder";

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  location: { zip: "", city: "" },
  layout: "",
  measurements: { width: "", height: "", ceiling: "", needHelp: false },
  currentWallPhoto: "", // storage path
  conditions: [],
  style: "",
  inspirationPhoto: "",
  tv: { status: "", size: "", mount: "" },
  mainFinish: "",
  accent: { material: "", side: "" },
  colors: [],
  trim: "",
  fireplace: { choice: "", width: "" },
  console: { choice: "", details: "" },
  towers: { choice: "", cubby: "" },
  lighting: { types: [], color: "" },
  equipment: [],
  budget: "",
  timing: "",
  helpMeChoose: [], // list of step ids customer wants help with
};

// ---- Small helpers -------------------------------------------------
function toggleInArray(arr, value) {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

// Accept "10' 6\"" / "10ft 6in" / "126" / "10.5" and return "10' 6\"" pretty string.
// Returns "" for empty / obviously invalid.
function prettyMeasurement(raw) {
  if (!raw) return "";
  const s = String(raw).trim();
  // Feet + inches shorthand:  10' 6"   |   10' 6   |   10ft 6in
  const ftIn = s.match(/^(\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*(\d+(?:\.\d+)?)?\s*(?:"|in|inches)?$/i);
  if (ftIn) {
    const ft = parseFloat(ftIn[1]);
    const inch = ftIn[2] ? parseFloat(ftIn[2]) : 0;
    return `${ft}' ${inch}"`;
  }
  // Inches only
  const inOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:"|in|inches)?$/i);
  if (inOnly) {
    const inches = parseFloat(inOnly[1]);
    if (inches >= 24) {
      const ft = Math.floor(inches / 12);
      const rem = Math.round((inches - ft * 12) * 10) / 10;
      return rem ? `${ft}' ${rem}"` : `${ft}' 0"`;
    }
    return `${inches}"`;
  }
  return s; // pass through, don't error — Rob confirms on consult
}

function formatMeasurements(m) {
  if (m.needHelp) return "Needs help measuring";
  const parts = [];
  const w = prettyMeasurement(m.width);
  const h = prettyMeasurement(m.height);
  const c = prettyMeasurement(m.ceiling);
  if (w) parts.push(`W ${w}`);
  if (h) parts.push(`H ${h}`);
  if (c) parts.push(`Ceiling ${c}`);
  return parts.join(" · ") || "—";
}

function labelOf(list, val) {
  return list.find((o) => o.value === val)?.label || val || "";
}

function summarize(state) {
  return {
    location: state.location.zip || state.location.city
      ? `${state.location.city}${state.location.city && state.location.zip ? ", " : ""}${state.location.zip}`
      : "",
    layout: labelOf(LAYOUT_OPTIONS, state.layout),
    measurements: formatMeasurements(state.measurements),
    conditions: state.conditions.map((v) => labelOf(CONDITION_OPTIONS, v)),
    style: labelOf(STYLE_OPTIONS, state.style),
    tv: state.tv.status
      ? `${labelOf(TV_STATUS_OPTIONS, state.tv.status)}${state.tv.size ? ` · ${state.tv.size}"` : ""}${state.tv.mount ? ` · ${labelOf(TV_MOUNT_OPTIONS, state.tv.mount)}` : ""}`
      : "",
    mainFinish: labelOf(MAIN_FINISH_OPTIONS, state.mainFinish),
    accent: state.accent.material
      ? `${labelOf(ACCENT_OPTIONS, state.accent.material)}${state.accent.side ? ` · ${labelOf(ACCENT_SIDE_OPTIONS, state.accent.side)}` : ""}`
      : "",
    colors: state.colors.map((v) => labelOf(COLOR_OPTIONS, v)),
    trim: labelOf(TRIM_OPTIONS, state.trim),
    fireplace: state.fireplace.choice
      ? `${labelOf(FIREPLACE_OPTIONS, state.fireplace.choice)}${state.fireplace.width ? ` · ${labelOf(FIREPLACE_WIDTH_OPTIONS, state.fireplace.width)}` : ""}`
      : "",
    console: state.console.choice
      ? `${labelOf(CONSOLE_OPTIONS, state.console.choice)}${state.console.details ? ` · ${state.console.details}` : ""}`
      : "",
    towers: state.towers.choice
      ? `${labelOf(TOWER_OPTIONS, state.towers.choice)}${state.towers.cubby ? ` · Cubby ${labelOf(CUBBY_PRESETS, state.towers.cubby)}` : ""}`
      : "",
    lighting: state.lighting.types.length
      ? `${state.lighting.types.map((v) => labelOf(LIGHTING_OPTIONS, v)).join(", ")}${state.lighting.color ? ` · ${labelOf(LIGHTING_COLOR_OPTIONS, state.lighting.color)}` : ""}`
      : "",
    equipment: state.equipment.map((v) => labelOf(EQUIPMENT_OPTIONS, v)),
    budget: labelOf(BUDGET_OPTIONS, state.budget),
    timing: labelOf(TIMING_OPTIONS, state.timing),
    helpMeChoose: state.helpMeChoose,
  };
}

// ---- File uploader --------------------------------------------------
function ContactField({ testid, type = "text", placeholder, value, onChange, error }) {
  return (
    <div>
      <input
        className={`input-dark ${error ? "border-red-500/70" : ""}`}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      {error && (
        <div className="text-xs text-red-400 mt-1" data-testid={`error-${testid}`}>
          {error}
        </div>
      )}
    </div>
  );
}


function HelpBanner({ onClick, active, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-full border text-sm transition-colors ${
        active
          ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F3E5AB]"
          : "border-[#3f3f46] text-[#D1D1D6] hover:border-[#AA8035]"
      }`}
    >
      <HelpCircle className="w-4 h-4" />
      {active ? "Rob will guide you on this step" : "Help me choose"}
    </button>
  );
}

// ---- Main page ------------------------------------------------------
export default function DesignMyWall() {
  usePageMeta({
    title: "Design My Wall | Custom Media Wall Builder — Swift Luxury Walls",
    description:
      "Design your custom media wall in minutes. Answer a few visual questions and get an AI render — free design consultation across Tampa Bay by Swift Luxury Walls.",
    canonicalPath: "/design-my-wall",
  });
  const [state, setState] = useState(INITIAL);
  const [mode, setMode] = useState("intro"); // 'intro' | 'full' | 'quick'
  const [stepIdx, setStepIdx] = useState(0);
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    project_address: "",
    preferred_contact: "phone",
    special_requests: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [render, setRender] = useState({ path: "", generating: false });
  const contentRef = useRef(null);

  const visibleSteps = useMemo(
    () => STEPS.filter((s) => !s.showIf || s.showIf(state)),
    [state]
  );
  const step = visibleSteps[stepIdx] || visibleSteps[0];

  useEffect(() => {
    // Clamp step index when visible-list changes (e.g., accent-wall selected)
    if (stepIdx >= visibleSteps.length) {
      setStepIdx(Math.max(0, visibleSteps.length - 1));
    }
    // scroll builder content to top on step change
    contentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [stepIdx, visibleSteps.length]);

  const update = (patch) => setState((s) => ({ ...s, ...patch }));
  const updateNested = (key, patch) =>
    setState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const toggleHelp = (id) =>
    setState((s) => ({ ...s, helpMeChoose: toggleInArray(s.helpMeChoose, id) }));

  const [errors, setErrors] = useState({});

  // Returns [ok, errorsObj]. When ok=false, errorsObj describes which field(s)
  // are missing so we can highlight them and tell the user exactly what to fix.
  const validateStep = () => {
    switch (step.id) {
      case "location": {
        if (state.helpMeChoose.includes("location")) return [true, {}];
        const errs = {};
        if (!state.location.city) errs["location-city"] = "Add your city.";
        if (!state.location.zip) errs["location-zip"] = "Add a zip code.";
        return [Object.keys(errs).length === 0, errs];
      }
      case "layout":
        if (state.helpMeChoose.includes("layout") || state.layout) return [true, {}];
        return [false, { layout: "Pick a starting layout — or tap ‘Help me choose’." }];
      case "measurements": {
        if (state.helpMeChoose.includes("measurements") || state.measurements.needHelp) return [true, {}];
        const errs = {};
        if (!state.measurements.width) errs["measure-width"] = "Add wall width.";
        if (!state.measurements.height) errs["measure-height"] = "Add wall height.";
        const ok = Object.keys(errs).length === 0;
        return [ok, ok ? {} : { ...errs, measurements: "Width and height are needed — or check ‘I need help measuring’." }];
      }
      case "conditions":
        return [true, {}];
      case "style":
        if (state.helpMeChoose.includes("style") || state.style) return [true, {}];
        return [false, { style: "Pick a style or tap ‘Help me choose’." }];
      case "tv":
        if (state.helpMeChoose.includes("tv") || state.tv.status) return [true, {}];
        return [false, { tv: "Tell us if you have a TV, or tap ‘Help me choose’." }];
      case "mainFinish":
        if (state.helpMeChoose.includes("mainFinish") || state.mainFinish) return [true, {}];
        return [false, { mainFinish: "Pick a main finish or tap ‘Help me choose’." }];
      case "accent":
        return [true, {}];
      case "colors":
        if (state.helpMeChoose.includes("colors") || state.colors.length > 0 || state.trim) return [true, {}];
        return [false, { colors: "Pick a color or trim — or tap ‘Help me choose’." }];
      case "fireplace":
      case "console":
      case "towers":
      case "extras":
        return [true, {}];
      case "lighting":
        return [true, {}];
      case "budget": {
        if (state.helpMeChoose.includes("budget")) return [true, {}];
        const errs = {};
        if (!state.budget) errs.budget = "Pick a budget range.";
        if (!state.timing) errs.timing = "Pick a timing option.";
        return [Object.keys(errs).length === 0, errs];
      }
      case "review": {
        const errs = {};
        if (!contact.name) errs["contact-name"] = "Add your full name.";
        if (!contact.email) errs["contact-email"] = "Add an email.";
        if (!contact.phone) errs["contact-phone"] = "Add a phone number.";
        if (!contact.project_address) errs["contact-address"] = "Add your city.";
        return [Object.keys(errs).length === 0, errs];
      }
      default:
        return [true, {}];
    }
  };

  const canProceed = () => validateStep()[0];

  const goNext = () => {
    const [ok, errs] = validateStep();
    if (!ok) {
      setErrors(errs);
      const first = Object.values(errs)[0];
      toast.error(first || "Please fill the required fields.");
      return;
    }
    setErrors({});
    if (stepIdx < visibleSteps.length - 1) setStepIdx(stepIdx + 1);
  };
  const goBack = () => {
    setErrors({});
    stepIdx > 0 && setStepIdx(stepIdx - 1);
  };
  const jumpTo = (id) => {
    setErrors({});
    const idx = visibleSteps.findIndex((s) => s.id === id);
    if (idx >= 0) setStepIdx(idx);
  };

  const submit = async () => {
    const [ok, errs] = validateStep();
    if (!ok) {
      setErrors(errs);
      toast.error(Object.values(errs)[0] || "Please complete your contact details.");
      return;
    }
    setErrors({});
    setSubmitting(true);
    const payload = {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      project_address: contact.project_address,
      preferred_contact: contact.preferred_contact,
      special_requests: contact.special_requests,
      selections: summarize(state),
      current_wall_photo: state.currentWallPhoto || null,
      inspiration_photo: state.inspirationPhoto || null,
      ai_render_photo: render.path || null,
    };
    try {
      const res = await axios.post(`${API}/api/wall-design`, payload);
      setSubmitted(res.data);
      toast.success("Design request sent — Rob will be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  const generateRender = async () => {
    setRender({ path: "", generating: true });
    try {
      const res = await axios.post(`${API}/api/design-render`, {
        selections: summarize(state),
      }, { timeout: 120000 });
      setRender({ path: res.data.path, generating: false });
      toast.success("Your AI design render is ready.");
    } catch (e) {
      setRender({ path: "", generating: false });
      toast.error(e?.response?.data?.detail || "Render failed. Please try again.");
    }
  };

  if (submitted) {
    return <SuccessScreen sub={submitted} />;
  }

  if (mode === "intro") {
    return (
      <IntroScreen
        onStartFull={() => setMode("full")}
        onStartQuick={() => setMode("quick")}
      />
    );
  }

  if (mode === "quick") {
    return (
      <QuickEstimate
        onBack={() => setMode("intro")}
        onSubmitted={(sub) => setSubmitted(sub)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-32 grain">
      <div className="container-x px-5 md:px-10 lg:px-16">
        {/* Header row */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/"
            data-testid="builder-home-link"
            className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#A1A1AA] hover:text-white"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Design My Wall</div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Main column */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            <ProgressBar current={stepIdx} total={visibleSteps.length} label={step.title} />
            <div ref={contentRef} className="mt-8">
              <h1 data-testid="step-title" className="font-display text-3xl md:text-4xl text-white leading-tight">
                {step.title}
              </h1>
              <p data-testid="step-subtitle" className="text-sm text-[#A1A1AA] mt-2">
                {step.subtitle}
              </p>

              <div className="mt-8">
                <StepBody
                  step={step}
                  state={state}
                  update={update}
                  updateNested={updateNested}
                  toggleHelp={toggleHelp}
                  contact={contact}
                  setContact={setContact}
                  jumpTo={jumpTo}
                  summary={summarize(state)}
                  render={render}
                  onGenerateRender={generateRender}
                  errors={errors}
                />
              </div>

              {/* Nav buttons */}
              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={stepIdx === 0}
                  data-testid="builder-back-btn"
                  className={`inline-flex items-center gap-2 px-5 py-3 rounded-full border text-sm transition-colors ${
                    stepIdx === 0
                      ? "border-[#1f1f22] text-[#3f3f46] cursor-not-allowed"
                      : "border-[#27272A] text-[#D1D1D6] hover:border-[#AA8035]"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>

                {step.id !== "review" ? (
                  <button
                    type="button"
                    onClick={goNext}
                    data-testid="builder-next-btn"
                    className="btn-gold !py-3 !px-6"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="builder-submit-btn"
                    className="btn-gold !py-3 !px-6"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Submitting
                      </>
                    ) : (
                      <>
                        Submit My Wall Design <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Side summary */}
          <aside className="lg:col-span-4 order-1 lg:order-2">
            <div className="sticky top-24">
              <SideSummary summary={summarize(state)} jumpTo={jumpTo} />
              <div className="mt-4 rounded-xl border border-[#1f1f22] p-4 text-xs text-[#A1A1AA] leading-relaxed">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-2">Need help?</div>
                Call Rob directly at{" "}
                <a href={`tel:${SITE.phoneDigits}`} className="text-[#F3E5AB]">
                  {SITE.phone}
                </a>{" "}
                or use the "Help me choose" option on any step and we'll cover it on the consult.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ---- Step body ------------------------------------------------------
function StepBody({ step, state, update, updateNested, toggleHelp, contact, setContact, jumpTo, summary, render, onGenerateRender, errors = {} }) {
  switch (step.id) {
    case "location":
      return (
        <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">City</label>
            <input
              className={`input-dark mt-2 ${errors["location-city"] ? "border-red-500/70" : ""}`}
              value={state.location.city}
              onChange={(e) => updateNested("location", { city: e.target.value })}
              placeholder="Tampa"
              data-testid="location-city"
            />
            {errors["location-city"] && (
              <div className="text-xs text-red-400 mt-1" data-testid="error-location-city">
                {errors["location-city"]}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">ZIP code</label>
            <input
              className={`input-dark mt-2 ${errors["location-zip"] ? "border-red-500/70" : ""}`}
              value={state.location.zip}
              onChange={(e) => updateNested("location", { zip: e.target.value })}
              placeholder="33602"
              inputMode="numeric"
              data-testid="location-zip"
            />
            {errors["location-zip"] && (
              <div className="text-xs text-red-400 mt-1" data-testid="error-location-zip">
                {errors["location-zip"]}
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <HelpBanner
              active={state.helpMeChoose.includes("location")}
              onClick={() => toggleHelp("location")}
              testid="help-location"
            />
          </div>
        </div>
      );

    case "layout":
      return (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LAYOUT_OPTIONS.map((o) => (
              <OptionCard
                key={o.value}
                selected={state.layout === o.value}
                onClick={() => update({ layout: o.value })}
                img={o.img}
                label={o.label}
                desc={o.desc}
                dataTestid={`layout-${o.value}`}
              />
            ))}
          </div>
          <HelpBanner
            active={state.helpMeChoose.includes("layout")}
            onClick={() => toggleHelp("layout")}
            testid="help-layout"
          />
        </>
      );

    case "measurements":
      return (
        <div className="space-y-6 max-w-2xl">
          {/* Simple illustration */}
          <div className="rounded-xl border border-[#27272A] bg-[#0c0c0c] p-4 flex items-start gap-4">
            <svg viewBox="0 0 120 80" className="flex-none w-24 h-16" aria-hidden="true">
              <rect x="6" y="6" width="108" height="68" fill="none" stroke="#3f3f46" strokeDasharray="3 3" />
              <rect x="18" y="18" width="84" height="44" fill="#111" stroke="#D4AF37" strokeWidth="1.5" />
              <line x1="18" y1="70" x2="102" y2="70" stroke="#D4AF37" strokeWidth="1" markerStart="url(#a)" markerEnd="url(#a)" />
              <line x1="8" y1="18" x2="8" y2="62" stroke="#D4AF37" strokeWidth="1" markerStart="url(#a)" markerEnd="url(#a)" />
              <line x1="112" y1="6" x2="112" y2="74" stroke="#6b6b72" strokeDasharray="2 2" />
              <text x="60" y="78" fontSize="6" fill="#D4AF37" textAnchor="middle" fontFamily="sans-serif">wall width</text>
              <text x="5" y="42" fontSize="6" fill="#D4AF37" fontFamily="sans-serif" transform="rotate(-90 5 42)" textAnchor="middle">wall height</text>
              <text x="115" y="42" fontSize="6" fill="#8b8b92" fontFamily="sans-serif" transform="rotate(-90 115 42)" textAnchor="middle">ceiling</text>
              <defs><marker id="a" markerWidth="3" markerHeight="3" refX="1.5" refY="1.5" orient="auto"><path d="M0,0 L3,1.5 L0,3 z" fill="#D4AF37" /></marker></defs>
            </svg>
            <div className="text-xs text-[#C4C4CC] leading-relaxed">
              <div className="text-white font-medium mb-1">How to measure</div>
              <p><span className="text-[#D4AF37]">Wall width</span> = side-to-side of the section you want built.</p>
              <p><span className="text-[#D4AF37]">Wall height</span> = floor up to where the build stops (usually just below the ceiling).</p>
              <p><span className="text-[#8b8b92]">Ceiling height</span> = full room ceiling. Enter feet + inches (e.g. <span className="text-white">10' 6"</span>) or just inches.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: "width", label: "Wall width", placeholder: `e.g. 10' 6" or 126"` },
              { key: "height", label: "Wall height", placeholder: `e.g. 8'` },
              { key: "ceiling", label: "Ceiling height", placeholder: `e.g. 9' 4"` },
            ].map((f) => (
              <div key={f.key}>
                <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">{f.label}</label>
                <input
                  className={`input-dark mt-2 ${errors[`measure-${f.key}`] ? "border-red-500/70" : ""}`}
                  disabled={state.measurements.needHelp}
                  value={state.measurements[f.key]}
                  onChange={(e) =>
                    updateNested("measurements", { [f.key]: e.target.value })
                  }
                  placeholder={f.placeholder}
                  inputMode="text"
                  data-testid={`measure-${f.key}`}
                />
              </div>
            ))}
          </div>
          {errors.measurements && (
            <div className="text-xs text-red-400" data-testid="error-measurements">{errors.measurements}</div>
          )}
          <label className="flex items-center gap-3 text-sm text-[#D1D1D6]">
            <input
              type="checkbox"
              checked={state.measurements.needHelp}
              onChange={(e) =>
                updateNested("measurements", { needHelp: e.target.checked })
              }
              data-testid="measure-need-help"
              className="w-4 h-4 accent-[#D4AF37]"
            />
            I need help measuring — Rob will bring a tape on the consult
          </label>
          <PhotoField
            label="Upload a current wall photo"
            hint="Helps us plan around outlets, doors and existing features."
            value={state.currentWallPhoto}
            onChange={(v) => update({ currentWallPhoto: v })}
            testid="upload-current-wall"
          />
        </div>
      );

    case "conditions":
      return (
        <>
          <div className="flex flex-wrap gap-2.5">
            {CONDITION_OPTIONS.map((o) => (
              <CompactChip
                key={o.value}
                selected={state.conditions.includes(o.value)}
                onClick={() => update({ conditions: toggleInArray(state.conditions, o.value) })}
                label={o.label}
                dataTestid={`cond-${o.value}`}
              />
            ))}
          </div>
          <div className="text-xs text-[#8b8b92] mt-3">Select all that apply.</div>
        </>
      );

    case "style":
      return (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
            {STYLE_OPTIONS.map((o) => (
              <OptionCard
                key={o.value}
                selected={state.style === o.value}
                onClick={() => update({ style: o.value })}
                img={o.img}
                label={o.label}
                desc={o.desc}
                dataTestid={`style-${o.value}`}
              />
            ))}
          </div>
          <div className="mt-6 max-w-md">
            <PhotoField
              label="Or upload inspiration"
              hint="A saved photo of a look you love — we'll draw from it."
              value={state.inspirationPhoto}
              onChange={(v) => update({ inspirationPhoto: v })}
              testid="upload-inspiration"
            />
          </div>
          <HelpBanner
            active={state.helpMeChoose.includes("style")}
            onClick={() => toggleHelp("style")}
            testid="help-style"
          />
        </>
      );

    case "tv":
      return (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-3 gap-3">
            {TV_STATUS_OPTIONS.map((o) => (
              <OptionCard
                key={o.value}
                selected={state.tv.status === o.value}
                onClick={() => updateNested("tv", { status: o.value })}
                label={o.label}
                desc={o.desc}
                dataTestid={`tv-${o.value}`}
              />
            ))}
          </div>
          {state.tv.status && state.tv.status !== "no-tv" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">TV size (inches)</label>
                <input
                  className="input-dark mt-2"
                  value={state.tv.size}
                  onChange={(e) => updateNested("tv", { size: e.target.value })}
                  placeholder="65"
                  inputMode="numeric"
                  data-testid="tv-size"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA]">Mount type</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TV_MOUNT_OPTIONS.map((o) => (
                    <CompactChip
                      key={o.value}
                      selected={state.tv.mount === o.value}
                      onClick={() => updateNested("tv", { mount: o.value })}
                      label={o.label}
                      dataTestid={`tv-mount-${o.value}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
          <HelpBanner
            active={state.helpMeChoose.includes("tv")}
            onClick={() => toggleHelp("tv")}
            testid="help-tv"
          />
        </div>
      );

    case "mainFinish":
      return (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MAIN_FINISH_OPTIONS.map((o) => (
              <OptionCard
                key={o.value}
                selected={state.mainFinish === o.value}
                onClick={() => update({ mainFinish: o.value })}
                img={o.img}
                label={o.label}
                desc={o.desc}
                dataTestid={`finish-${o.value}`}
              />
            ))}
          </div>
          <HelpBanner
            active={state.helpMeChoose.includes("mainFinish")}
            onClick={() => toggleHelp("mainFinish")}
            testid="help-finish"
          />
        </>
      );

    case "accent":
      return (
        <div className="space-y-6 max-w-2xl">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Accent material</div>
            <div className="flex flex-wrap gap-2">
              {ACCENT_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.accent.material === o.value}
                  onClick={() => updateNested("accent", { material: o.value })}
                  label={o.label}
                  dataTestid={`accent-${o.value}`}
                />
              ))}
            </div>
          </div>
          {state.accent.material && state.accent.material !== "none" && (
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Place on</div>
              <div className="flex flex-wrap gap-2">
                {ACCENT_SIDE_OPTIONS.map((o) => (
                  <CompactChip
                    key={o.value}
                    selected={state.accent.side === o.value}
                    onClick={() => updateNested("accent", { side: o.value })}
                    label={o.label}
                    dataTestid={`accent-side-${o.value}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      );

    case "colors":
      return (
        <div className="space-y-6 max-w-2xl">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Palette</div>
            <div className="flex flex-wrap gap-2.5">
              {COLOR_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.colors.includes(o.value)}
                  onClick={() => update({ colors: toggleInArray(state.colors, o.value) })}
                  label={o.label}
                  swatch={o.swatch}
                  dataTestid={`color-${o.value}`}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Trim</div>
            <div className="flex flex-wrap gap-2.5">
              {TRIM_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.trim === o.value}
                  onClick={() => update({ trim: o.value })}
                  label={o.label}
                  swatch={o.swatch}
                  dataTestid={`trim-${o.value}`}
                />
              ))}
            </div>
          </div>
          <HelpBanner
            active={state.helpMeChoose.includes("colors")}
            onClick={() => toggleHelp("colors")}
            testid="help-colors"
          />
        </div>
      );

    case "extras":
      return (
        <div className="space-y-8 max-w-2xl">
          {/* Fireplace */}
          <section className="space-y-4">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Fireplace</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {FIREPLACE_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  selected={state.fireplace.choice === o.value}
                  onClick={() => updateNested("fireplace", { choice: o.value })}
                  label={o.label}
                  dataTestid={`fp-${o.value}`}
                />
              ))}
            </div>
            {state.fireplace.choice && state.fireplace.choice !== "none" && (
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC] mb-3">Width</div>
                <div className="flex flex-wrap gap-2">
                  {FIREPLACE_WIDTH_OPTIONS.map((o) => (
                    <CompactChip
                      key={o.value}
                      selected={state.fireplace.width === o.value}
                      onClick={() => updateNested("fireplace", { width: o.value })}
                      label={o.label}
                      dataTestid={`fp-width-${o.value}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Console */}
          <section className="space-y-4 border-t border-[#1a1a1a] pt-8">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Console & storage</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {CONSOLE_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  selected={state.console.choice === o.value}
                  onClick={() => updateNested("console", { choice: o.value })}
                  label={o.label}
                  dataTestid={`console-${o.value}`}
                />
              ))}
            </div>
            {state.console.choice && !["none", "existing"].includes(state.console.choice) && (
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC]">
                  Width, finish, doors / drawers / open (freeform)
                </label>
                <textarea
                  className="input-dark mt-2 min-h-[100px]"
                  value={state.console.details}
                  onChange={(e) => updateNested("console", { details: e.target.value })}
                  placeholder='e.g. 72" wide, walnut finish, 2 doors + open center bay'
                  data-testid="console-details"
                />
              </div>
            )}
          </section>

          {/* Towers */}
          <section className="space-y-4 border-t border-[#1a1a1a] pt-8">
            <div className="text-xs tracking-[0.2em] uppercase text-[#D4AF37]">Towers & shelving</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {TOWER_OPTIONS.map((o) => (
                <OptionCard
                  key={o.value}
                  selected={state.towers.choice === o.value}
                  onClick={() => updateNested("towers", { choice: o.value })}
                  label={o.label}
                  dataTestid={`tower-${o.value}`}
                />
              ))}
            </div>
            {state.towers.choice === "bookcase" && (
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-[#C4C4CC] mb-3">Cubby preset</div>
                <div className="flex flex-wrap gap-2">
                  {CUBBY_PRESETS.map((o) => (
                    <CompactChip
                      key={o.value}
                      selected={state.towers.cubby === o.value}
                      onClick={() => updateNested("towers", { cubby: o.value })}
                      label={o.label}
                      dataTestid={`cubby-${o.value}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <HelpBanner
            active={state.helpMeChoose.includes("extras")}
            onClick={() => toggleHelp("extras")}
            testid="help-extras"
          />
        </div>
      );

    case "lighting":
      return (
        <div className="space-y-6 max-w-2xl">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Lighting</div>
            <div className="flex flex-wrap gap-2.5">
              {LIGHTING_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.lighting.types.includes(o.value)}
                  onClick={() =>
                    updateNested("lighting", {
                      types: toggleInArray(state.lighting.types, o.value),
                    })
                  }
                  label={o.label}
                  dataTestid={`light-${o.value}`}
                />
              ))}
            </div>
          </div>
          {state.lighting.types.length > 0 && !state.lighting.types.includes("none") && (
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Color</div>
              <div className="flex flex-wrap gap-2.5">
                {LIGHTING_COLOR_OPTIONS.map((o) => (
                  <CompactChip
                    key={o.value}
                    selected={state.lighting.color === o.value}
                    onClick={() => updateNested("lighting", { color: o.value })}
                    label={o.label}
                    dataTestid={`light-color-${o.value}`}
                  />
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Equipment</div>
            <div className="flex flex-wrap gap-2.5">
              {EQUIPMENT_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.equipment.includes(o.value)}
                  onClick={() => update({ equipment: toggleInArray(state.equipment, o.value) })}
                  label={o.label}
                  dataTestid={`equip-${o.value}`}
                />
              ))}
            </div>
          </div>
        </div>
      );

    case "budget":
      return (
        <div className="space-y-6 max-w-2xl">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Budget range</div>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {BUDGET_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.budget === o.value}
                  onClick={() => update({ budget: o.value })}
                  label={o.label}
                  dataTestid={`budget-${o.value}`}
                />
              ))}
            </div>
            <div className="text-[11px] text-[#8b8b92] mt-3">
              Ranges are qualification only — not an automatic quote.
            </div>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Timing</div>
            <div className="flex flex-wrap gap-2.5">
              {TIMING_OPTIONS.map((o) => (
                <CompactChip
                  key={o.value}
                  selected={state.timing === o.value}
                  onClick={() => update({ timing: o.value })}
                  label={o.label}
                  dataTestid={`timing-${o.value}`}
                />
              ))}
            </div>
          </div>
        </div>
      );

    case "review":
      return (
        <ReviewStep
          summary={summary}
          contact={contact}
          setContact={setContact}
          jumpTo={jumpTo}
          helpMeChoose={state.helpMeChoose}
          render={render}
          onGenerateRender={onGenerateRender}
          errors={errors}
        />
      );

    default:
      return null;
  }
}

// ---- Review + Contact ----------------------------------------------
function ReviewStep({ summary, contact, setContact, jumpTo, helpMeChoose, render, onGenerateRender, errors = {} }) {
  const rows = [
    ["location", "Location"],
    ["layout", "Starting layout"],
    ["measurements", "Measurements"],
    ["conditions", "Existing conditions"],
    ["style", "Style"],
    ["tv", "TV setup"],
    ["mainFinish", "Main finish"],
    ["accent", "Accent"],
    ["colors", "Colors"],
    ["trim", "Trim"],
    ["fireplace", "Fireplace"],
    ["console", "Console"],
    ["towers", "Towers / shelving"],
    ["lighting", "Lighting"],
    ["equipment", "Equipment"],
    ["budget", "Budget"],
    ["timing", "Timing"],
  ];
  return (
    <div className="space-y-8">
      <div className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Your selections</div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {rows.map(([k, label]) => {
            const value = summary[k];
            const empty = !value || (Array.isArray(value) && !value.length);
            return (
              <button
                type="button"
                key={k}
                onClick={() => jumpTo(k)}
                data-testid={`review-jump-${k}`}
                className="text-left group"
              >
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#8b8b92] group-hover:text-[#D4AF37]">
                  {label}
                </div>
                <div className={`text-sm mt-1 ${empty ? "text-[#3f3f46]" : "text-white"}`}>
                  {empty ? "—" : Array.isArray(value) ? value.join(", ") : value}
                </div>
              </button>
            );
          })}
        </div>
        {helpMeChoose.length > 0 && (
          <div className="mt-6 pt-6 border-t border-[#1a1a1a]">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Guidance requested</div>
            <div className="text-xs text-[#A1A1AA] mt-2">
              Rob will walk you through: {helpMeChoose.join(", ")}
            </div>
          </div>
        )}
      </div>

      {/* AI Render */}
      <div className="rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0f0d06] via-[#0d0d0d] to-[#050505] p-6 md:p-8 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ background: "radial-gradient(circle at 100% 0%, rgba(212,175,55,0.18) 0%, transparent 55%)" }}
        />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] flex items-center gap-2">
                <Wand2 className="w-3.5 h-3.5" /> AI wall render
              </div>
              <div className="font-display text-2xl md:text-3xl text-white mt-2 leading-tight">
                See your design before it's built
              </div>
              <div className="text-sm text-[#A1A1AA] mt-2 max-w-xl">
                We'll turn your selections into a photorealistic preview — takes about 30–60 seconds. Free with your design request.
              </div>
            </div>
            {!render.generating && (
              <button
                type="button"
                onClick={onGenerateRender}
                data-testid="ai-render-generate"
                className="btn-gold !py-3 !px-5"
              >
                {render.path ? (
                  <>
                    <RefreshCw className="w-4 h-4" /> Regenerate
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" /> Generate my render
                  </>
                )}
              </button>
            )}
          </div>

          <div className="mt-6 rounded-xl border border-[#27272A] overflow-hidden bg-[#050505] min-h-[240px] flex items-center justify-center">
            {render.generating ? (
              <div className="text-center py-14 px-6">
                <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin mx-auto" />
                <div className="text-sm text-white mt-4">Rendering your wall…</div>
                <div className="text-xs text-[#A1A1AA] mt-2">
                  Composing marble, slats, lighting and finishes. ~30–60 seconds.
                </div>
              </div>
            ) : render.path ? (
              <img
                src={`${API}/api/files/${render.path}`}
                alt="AI render of your wall"
                data-testid="ai-render-image"
                className="w-full h-auto max-h-[560px] object-contain"
              />
            ) : (
              <div className="text-center py-14 px-6 text-[#8b8b92] text-sm">
                Tap "Generate my render" and we'll paint your wall right here.
              </div>
            )}
          </div>
          {render.path && (
            <div className="text-[11px] text-[#8b8b92] mt-3 leading-relaxed">
              This is an AI-generated preview, not a final rendering. Rob will confirm exact materials, dimensions and finish on the consult.
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Your details</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <ContactField
            testid="contact-name"
            placeholder="Full name"
            value={contact.name}
            onChange={(v) => setContact({ ...contact, name: v })}
            error={errors["contact-name"]}
          />
          <ContactField
            testid="contact-email"
            type="email"
            placeholder="Email"
            value={contact.email}
            onChange={(v) => setContact({ ...contact, email: v })}
            error={errors["contact-email"]}
          />
          <ContactField
            testid="contact-phone"
            placeholder="Phone"
            value={contact.phone}
            onChange={(v) => setContact({ ...contact, phone: v })}
            error={errors["contact-phone"]}
          />
          <ContactField
            testid="contact-address"
            placeholder="City"
            value={contact.project_address}
            onChange={(v) => setContact({ ...contact, project_address: v })}
            error={errors["contact-address"]}
          />
        </div>
        <div className="mt-5">
          <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Preferred contact</div>
          <div className="flex flex-wrap gap-2.5">
            {CONTACT_METHODS.map((o) => (
              <CompactChip
                key={o.value}
                selected={contact.preferred_contact === o.value}
                onClick={() => setContact({ ...contact, preferred_contact: o.value })}
                label={o.label}
                dataTestid={`contact-pref-${o.value}`}
              />
            ))}
          </div>
        </div>
        <div className="mt-5">
          <textarea
            className="input-dark min-h-[100px]"
            placeholder="Anything else we should know? (optional)"
            value={contact.special_requests}
            onChange={(e) => setContact({ ...contact, special_requests: e.target.value })}
            data-testid="contact-requests"
          />
        </div>
      </div>
    </div>
  );
}

function SideSummary({ summary, jumpTo }) {
  const items = [
    ["layout", "Layout"],
    ["style", "Style"],
    ["mainFinish", "Finish"],
    ["fireplace", "Fireplace"],
    ["budget", "Budget"],
  ];
  return (
    <div className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-5">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" /> Your design
      </div>
      <div className="space-y-3">
        {items.map(([k, label]) => (
          <button
            type="button"
            key={k}
            onClick={() => jumpTo(k)}
            className="w-full text-left flex items-baseline gap-3 group"
          >
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#8b8b92] w-16 flex-none group-hover:text-[#D4AF37]">
              {label}
            </div>
            <div className={`text-sm ${summary[k] ? "text-white" : "text-[#3f3f46]"}`}>
              {summary[k] || "—"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function SuccessScreen({ sub }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white grain flex items-center justify-center px-6 pt-24 pb-16">
      <div className="max-w-xl text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-8">
          <CheckCircle2 className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <h1 className="font-display text-4xl md:text-5xl">Design received</h1>
        <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37] mt-3">
          #{sub.id.slice(0, 8).toUpperCase()} · Final scope & pricing pending review
        </div>
        <p className="text-sm text-[#A1A1AA] mt-6 leading-relaxed">
          Thanks, {sub.name.split(" ")[0]}. Rob will personally review your selections and reach out to
          confirm dimensions, finishes and next steps — usually within one business day.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <a href={`tel:${SITE.phoneDigits}`} className="btn-gold !py-3 !px-6">
            <Phone className="w-4 h-4" /> Call {SITE.phone}
          </a>
          <button
            type="button"
            className="btn-ghost !py-3 !px-6"
            onClick={() => navigate("/")}
          >
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
